import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Components
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FlagCountry from 'src/components/common/FlagCountry';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import Table from 'src/components/Table';

// Utilities
import dayjs from 'dayjs';
import { includes, isEmpty, isEqual, map, startCase } from 'lodash';
import { useMemo, useState } from 'react';
import { DownloadCloud, Info } from 'react-feather';
import IconFeather from 'src/components/common/Icon';
import TooltipExplainNoTradeValue from 'src/components/common/TooltipExplainNoTradeValue';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipTradeDataLimit from 'src/components/common/TooltipTradeDataLimit';
import LinkToCompanyDetail from 'src/components/LinkToCompanyDetail';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import PopupExportBOLs from 'src/features/TradeData/TradeData/PopupExportBOLs';
import useCreditFlow from 'src/hooks/useCreditFlow';
import { LIST_RESTRICTION_DATA, useProvisionRestriction } from 'src/hooks/useProvisionRestriction';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryListBOLs } from 'src/services/queries/useQueryTradeData';
import { BAD_RECORD_NAMES } from 'src/utils/constants/badRecordNames';
import {
  FORMAT_DATE_TIME,
  LIMIT_GET_LIST_BILLS_OF_LADING,
  WITHHELD_GOVERNMENT_COMPANY_NAME,
} from 'src/utils/constants/config';
import { FilterWithheld, KeyFilterChartTradeDataUnitBy, TypeBuyerSupplier } from 'src/utils/constants/enum';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { checkCountryHaveState, renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';
import { ISearchGlobalTradeDataQueryParams } from 'src/utils/typings/dto/tradeData';
import { useFormSearch } from 'src/zustand/useFormSearch';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

const TradeData = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { checkRestriction } = useProvisionRestriction();
  const queryTradeData = useQueryParams() as ISearchGlobalTradeDataQueryParams;
  const [openExport, setOpenExport] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const { handleOpenSearch } = useFormSearch((state) => state);

  const { setSort, billsOfLadingListSortParams } = useTradeDataSortStore();

  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);

  const showCompanyName = (name: string, data?: IBuyerSupplierDetail) => {
    if (!data) return;
    const isCountryHaveState = checkCountryHaveState(data?.country_code || '');

    if (isCountryHaveState && !isEmpty(data.jurisdiction)) return `${name} - (${data.jurisdiction})`;

    return name;
  };

  const bolRequestPayload = useMemo(() => {
    return {
      hs_code: queryTradeData.hs_code,
      keyword: queryTradeData.keyword,
      supplier_keyword: queryTradeData.supplier_keyword,
      buyer_keyword: queryTradeData.buyer_keyword,
      start_date: queryTradeData.start_date,
      end_date: queryTradeData.end_date,
      exp_country_code: queryTradeData.exp_country_code,
      imp_country_code: queryTradeData.imp_country_code,
      exp_port_code: queryTradeData.exp_port_code,
      imp_port_code: queryTradeData.imp_port_code,
      supplier_id: queryTradeData.supplier_id,
      buyer_id: queryTradeData.buyer_id,
      offset: queryTradeData.offset || 0,
      limit: queryTradeData.limit || LIMIT_GET_LIST_BILLS_OF_LADING,
      sort_by: billsOfLadingListSortParams?.sortBy,
      order: billsOfLadingListSortParams?.sort,
      hide_withheld_government: queryTradeData?.hide_withheld_government || FilterWithheld.NOT_ALLOWED,
    };
  }, [queryTradeData]);

  // const transactionsPaymentStatusResp = useQueryTransactionsPaymentStatus(bolRequestPayload);

  const { data, dataMapping, isFetching } = useQueryListBOLs(
    acceptCreditPayment ? queryString.stringify(bolRequestPayload) : '',
  );

  const [shouldShowBillableDataLoading, hasEnoughCredit] = useCreditFlow({
    response: data,
    featureInfo: {
      featureId: LIST_FEATURES.TRADE_DATA_BILL_OF_LADING,
      action: LIST_ACTIONS.VIEW,
    },
    // paymentStatus: transactionsPaymentStatusResp.data?.data?.bol,
    // isCollectingData: transactionsPaymentStatusResp.isFetching,
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });

  const listBOLs = getArrayDefault(dataMapping);
  const redirectBolDetail = (item: ITradeData) => {
    navigate(AuthRouteKeys.BillOfLadingDetail.replace(':id?', item?.transaction.id));
  };

  const configColumn: ConfigCell<ITradeData>[] = [
    {
      disablePadding: false,
      id: 'bol',
      label: 'Bill of Lading',
      onClick: (data) => redirectBolDetail(data),
      cell: (data: ITradeData) => {
        const checkRestrictionBol = checkRestriction({
          countryCode: data?.buyer?.country_code || '',
          typeData: LIST_RESTRICTION_DATA.BOL,
        });
        return (
          <Typography variant="body2" color="secondary">
            {renderTextOrDefault(data?.transaction?.bol)}
            {(isEmpty(data?.transaction?.bol) || includes(BAD_RECORD_NAMES, data?.transaction?.bol)) &&
              checkRestrictionBol && <TooltipTradeDataLimit />}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'supplier_name',
      label: 'Suppliers',
      cell: (data: ITradeData) => {
        const isWithheldByGovernment = isEqual(data.supplier_name, WITHHELD_GOVERNMENT_COMPANY_NAME);
        return (
          <Stack alignItems={'center'} direction="row" spacing={0.5}>
            {isWithheldByGovernment ? (
              <IconFeather muiProps={{ fontSize: 'small', color: 'action' }} icon={<Info />} />
            ) : (
              <FlagCountry code={data?.supplier?.country_code || ''} />
            )}
            <LinkToCompanyDetail
              id={data?.supplier?.id}
              companyName={showCompanyName(data.supplier_name, data?.supplier || undefined) || ''}
              params={queryTradeData}
              viewedAsType={TypeBuyerSupplier.supplier}
            />
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'buyer_name',
      label: 'Buyers',
      cell: (data: ITradeData) => {
        const isWithheldByGovernment = isEqual(data.supplier_name, WITHHELD_GOVERNMENT_COMPANY_NAME);
        return (
          <Stack alignItems={'center'} direction="row" spacing={0.5}>
            {isWithheldByGovernment ? (
              <IconFeather muiProps={{ fontSize: 'small', color: 'action' }} icon={<Info />} />
            ) : (
              <FlagCountry code={data?.buyer?.country_code || ''} />
            )}
            <LinkToCompanyDetail
              id={data?.buyer?.id}
              companyName={showCompanyName(data.buyer_name, data?.buyer || undefined) || ''}
              params={queryTradeData}
              viewedAsType={TypeBuyerSupplier.buyer}
            />
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: ITradeData) => (
        <Typography variant="body2" color="text.secondary">
          {formatValueUSD(data.transaction.value_usd)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'actual_arrival_date',
      label: 'Date',
      cell: (data: ITradeData) => (
        <Typography variant="body2" color="text.secondary">
          {dayjs(data?.transaction?.actual_arrival_date?.toString(), FORMAT_DATE_TIME.yyyy_mm_dd).format(
            FORMAT_DATE_TIME.FULL_ONLY_DATE,
          )}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: 'Weight (KG)',
      cell: (data: ITradeData) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data?.transaction?.weight)}
          {isEqual(data?.transaction?.weight, 0) && <TooltipExplainNoTradeValue />}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: 'Quantity',
      cell: (data: ITradeData) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderNumberOrDefault(data?.transaction?.quantity)}
          {isEqual(data?.transaction?.quantity, 0) && <TooltipExplainNoTradeValue />}
        </Typography>
      ),
    },
  ];

  return (
    <WhiteBoxContent>
      <PopupExportBOLs
        sortBy={billsOfLadingListSortParams?.sortBy as KeyFilterChartTradeDataUnitBy}
        order={billsOfLadingListSortParams?.sort as 'asc' | 'desc'}
        open={openExport}
        totalRecordsAvailable={data?.total_records || 0}
        handleClose={() => setOpenExport(false)}
      />
      <Stack spacing={2} alignItems="center" flexWrap={'wrap'} justifyContent={'space-between'} flexDirection={'row'}>
        <Typography textAlign={'left'} variant="h5">
          Most Recent Bills Of Lading
        </Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            disabled={isFetching}
            onChange={(e) => {
              setSearchParams((searchParams) => {
                searchParams.set('hide_withheld_government', String(e.target.value));
                return searchParams;
              });
            }}
            helperText={
              <Typography variant="caption" color="text.secondary">
                What is Withheld by Government?
                <TooltipTradeDataLimit icon={<HelpOutlineIcon color="action" sx={{ fontSize: 16 }} />} />
              </Typography>
            }
            value={queryTradeData?.hide_withheld_government || FilterWithheld.NOT_ALLOWED}
            defaultValue={FilterWithheld.NOT_ALLOWED}
            select
            size="small"
            color="primary"
          >
            {map(Object.values(FilterWithheld), (option) => (
              <MenuItem key={option} value={option}>
                {startCase(t(`trade_data.transactions.filter_withheld.${option}`))}
              </MenuItem>
            ))}
          </TextField>
          <Button
            onClick={() => {
              setOpenExport(true);
            }}
            sx={{
              height: 'fit-content',
              padding: '8px 10px',
            }}
            color="primary"
            size="small"
            startIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<DownloadCloud />} />}
            variant="outlined"
          >
            Export
          </Button>
        </Stack>
      </Stack>
      <Box mt={3} />
      {!isFetching && isEmpty(getArrayDefault(listBOLs)) ? (
        <CompanyInfoNoData
          title={hasEnoughCredit ? t('trade_data.common.notice_no_data.title') : 'Not Enough Credit'}
          content={
            <>
              {hasEnoughCredit
                ? t('trade_data.common.notice_no_data.sub_title')
                : 'Can not access the data, please upgrade your plan'}
            </>
          }
        />
      ) : (
        <Table
          maxRow={100}
          rowsPerPageOptions={[10, 50, 100]}
          handleSort={(data) => {
            setSort('billsOfLadingListSortParams', data);
          }}
          onChangePage={(value) => {
            setSearchParams((searchParams) => {
              searchParams.set('offset', String(value));
              return searchParams;
            });
          }}
          onChangePageSize={(value) =>
            setSearchParams((searchParams) => {
              searchParams.set('offset', '0');
              searchParams.set('limit', String(value));
              return searchParams;
            })
          }
          // onRowClick={redirectBolDetail}
          loading={shouldShowBillableDataLoading || isFetching}
          page={Number(queryTradeData.offset || 0)}
          pageSize={Number(queryTradeData.limit || LIMIT_GET_LIST_BILLS_OF_LADING)}
          totalRecords={data?.total_records}
          dataKey={(item) => item.transaction.id}
          data={listBOLs}
          configColumn={configColumn}
        />
      )}
    </WhiteBoxContent>
  );
};

export default TradeData;
